/**
 * Thread, article, asset and collection permalinks used to embed the title in
 * the path as `<slugified-title>-<base64uuid>`, so for a search thread the
 * user's query is visible in the URL. New permalinks are bare UUIDs, but the
 * legacy form still resolves and is still what the address bar shows for
 * pre-migration links.
 *
 * This is the TypeScript twin of `uuid_to_slug` / `maybe_slug_to_uuid` in
 * `pplx/common/core/url_utils/__init__.py`. Keep the two in step: the client
 * canonicalizer uses it for legacy URLs and the backend still accepts both
 * forms.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isUuid(value) {
    return UUID_PATTERN.test(value);
}
/**
 * Encode a UUID into a URL-safe base64 string for use in share URLs.
 *
 * 1. Parse UUID hex → 16 bytes
 * 2. Base64 URL-safe encode
 * 3. Strip padding
 * 4. Replace "-" with "." ("-" breaks the URL slug parser)
 */
export function uuidToSlug(uuid) {
    const hex = uuid.replace(/-/g, '');
    const bytes = Uint8Array.from(hex.match(/.{2}/g), (h) => parseInt(h, 16));
    return btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
        .replace(/-/g, '.');
}
/**
 * Decode a URL-safe base64 slug back into a UUID string.
 * Inverse of `uuidToSlug`. Returns null if the slug is invalid.
 */
export function slugToUuid(slug) {
    try {
        const base64url = slug.replace(/\./g, '-');
        const padded = base64url + '='.repeat((4 - (base64url.length % 4)) % 4);
        const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
        const binary = atob(base64);
        if (binary.length !== 16)
            return null;
        const hex = Array.from(binary, (ch) => ch.charCodeAt(0).toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    catch {
        return null;
    }
}
/**
 * Resolve one permalink path segment to the UUID it denotes, accepting either
 * the canonical bare UUID or the legacy `<slugified-title>-<base64uuid>` form.
 * Returns null when the segment is neither, so an unrecognised path shape can
 * be left alone rather than rewritten. Mirrors `maybe_slug_to_uuid`.
 */
export function maybeSlugToUuid(segment) {
    if (isUuid(segment)) {
        return segment.toLowerCase();
    }
    // `uuidToSlug` rewrites "-" to "." inside the base64 token, so however many
    // dashes the slugified title contributed, the tail after the final "-" is the
    // whole token.
    return slugToUuid(segment.slice(segment.lastIndexOf('-') + 1));
}
//# sourceMappingURL=permalinkSlug.js.map